export class PharmacyAPI {
  constructor(apiClient) {
    this.apiClient = apiClient;
  }

  _qs(params) {
    const clean = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
    );
    return new URLSearchParams(clean).toString();
  }

  // ── Categories ──────────────────────────────────────────

  async listCategories(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/mm/category/list${qs ? `?${qs}` : ''}`);
  }

  async getCategoryById(id) {
    return this.apiClient.get(`/api/v1/pharmacy/mm/category/${id}`);
  }

  async createCategory(data) {
    return this.apiClient.post('/api/v1/pharmacy/mm/category/create', data);
  }

  async updateCategory(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/mm/category/${id}`, data);
  }

  async deleteCategory(id) {
    return this.apiClient.delete(`/api/v1/pharmacy/mm/category/${id}`);
  }

  // ── Units ───────────────────────────────────────────────

  async listUnits(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/mm/unit/list${qs ? `?${qs}` : ''}`);
  }

  async createUnit(data) {
    return this.apiClient.post('/api/v1/pharmacy/mm/unit/create', data);
  }

  async updateUnit(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/mm/unit/${id}`, data);
  }

  // ── HSN Codes ───────────────────────────────────────────

  async listHsn(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/mm/hsn/list${qs ? `?${qs}` : ''}`);
  }

  async getHsnById(id) {
    return this.apiClient.get(`/api/v1/pharmacy/mm/hsn/${id}`);
  }

  async createHsn(data) {
    return this.apiClient.post('/api/v1/pharmacy/mm/hsn/create', data);
  }

  async updateHsn(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/mm/hsn/${id}`, data);
  }

  // ── Manufacturers ───────────────────────────────────────

  async listManufacturers(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/mm/manufacturer/list${qs ? `?${qs}` : ''}`);
  }

  async getManufacturerById(id) {
    return this.apiClient.get(`/api/v1/pharmacy/mm/manufacturer/${id}`);
  }

  async createManufacturer(data) {
    return this.apiClient.post('/api/v1/pharmacy/mm/manufacturer/create', data);
  }

  async updateManufacturer(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/mm/manufacturer/${id}`, data);
  }

  // ── Suppliers ───────────────────────────────────────────

  async listSuppliers(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/mm/supplier/list${qs ? `?${qs}` : ''}`);
  }

  async getSupplierById(id) {
    return this.apiClient.get(`/api/v1/pharmacy/mm/supplier/${id}`);
  }

  async createSupplier(data) {
    return this.apiClient.post('/api/v1/pharmacy/mm/supplier/create', data);
  }

  async updateSupplier(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/mm/supplier/${id}`, data);
  }

  // ── Items ───────────────────────────────────────────────

  async listItems(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/mm/item/list${qs ? `?${qs}` : ''}`);
  }

  async getItemById(id) {
    return this.apiClient.get(`/api/v1/pharmacy/mm/item/${id}`);
  }

  async createItem(data) {
    return this.apiClient.post('/api/v1/pharmacy/mm/item/create', data);
  }

  async updateItem(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/mm/item/${id}`, data);
  }

  async deleteItem(id) {
    return this.apiClient.delete(`/api/v1/pharmacy/mm/item/${id}`);
  }

  // ── Variants ────────────────────────────────────────────

  async addVariant(itemId, data) {
    return this.apiClient.post(`/api/v1/pharmacy/mm/item/${itemId}/variant`, data);
  }

  async updateVariant(itemId, variantId, data) {
    return this.apiClient.put(`/api/v1/pharmacy/mm/item/${itemId}/variant/${variantId}`, data);
  }

  async removeVariant(itemId, variantId) {
    return this.apiClient.delete(`/api/v1/pharmacy/mm/item/${itemId}/variant/${variantId}`);
  }

  // ── Suppliers (item link) ────────────────────────────────

  async linkSupplier(itemId, data) {
    return this.apiClient.post(`/api/v1/pharmacy/mm/item/${itemId}/supplier`, data);
  }

  async updateSupplierLink(itemId, mappingId, data) {
    return this.apiClient.put(`/api/v1/pharmacy/mm/item/${itemId}/supplier/${mappingId}`, data);
  }

  async unlinkSupplier(itemId, mappingId) {
    return this.apiClient.delete(`/api/v1/pharmacy/mm/item/${itemId}/supplier/${mappingId}`);
  }

  // ── Prices ──────────────────────────────────────────────

  async getPriceHistory(itemId) {
    return this.apiClient.get(`/api/v1/pharmacy/mm/item/${itemId}/price/history`);
  }

  async setPrice(itemId, data) {
    return this.apiClient.post(`/api/v1/pharmacy/mm/item/${itemId}/price`, data);
  }

  async updatePrice(itemId, priceId, data) {
    return this.apiClient.put(`/api/v1/pharmacy/mm/item/${itemId}/price/${priceId}`, data);
  }

  async deletePrice(itemId, priceId) {
    return this.apiClient.delete(`/api/v1/pharmacy/mm/item/${itemId}/price/${priceId}`);
  }

  // ── Pharmacies (master) ───────────────────────────────────

  async listPharmacies() {
    return this.apiClient.get('/api/v1/pharmacy/pharmacies');
  }

  async createPharmacy(data) {
    return this.apiClient.post('/api/v1/pharmacy/pharmacies', data);
  }

  async updatePharmacy(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/pharmacies/${id}`, data);
  }

  async deletePharmacy(id) {
    return this.apiClient.delete(`/api/v1/pharmacy/pharmacies/${id}`);
  }

  // ── Store Locations ──────────────────────────────────────

  async listLocations(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/locations${qs ? `?${qs}` : ''}`);
  }

  async createLocation(data) {
    return this.apiClient.post('/api/v1/pharmacy/locations', data);
  }

  async updateLocation(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/locations/${id}`, data);
  }

  // ── Inward Invoices ──────────────────────────────────────

  async listInward(params = {}) {
    const qs = this._qs(params); // supports pharmacy_id, status, search, limit, offset
    return this.apiClient.get(`/api/v1/pharmacy/inward/list${qs ? `?${qs}` : ''}`);
  }

  async getInward(id) {
    return this.apiClient.get(`/api/v1/pharmacy/inward/${id}`);
  }

  async createInward(data) {
    return this.apiClient.post('/api/v1/pharmacy/inward/create', data);
  }

  async updateInward(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/inward/${id}`, data);
  }

  async confirmInward(id) {
    return this.apiClient.put(`/api/v1/pharmacy/inward/${id}/confirm`);
  }

  async cancelInward(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/inward/${id}/cancel`, data);
  }

  // ── Payment ──────────────────────────────────────────────

  async getInwardPayment(id) {
    return this.apiClient.get(`/api/v1/pharmacy/inward/${id}/payment`);
  }

  async updateInwardPayment(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/inward/${id}/payment`, data);
  }

  async uploadPaymentPod(id, formData) {
    return this.apiClient.post(`/api/v1/pharmacy/inward/${id}/payment/upload`, formData);
  }

  // ── Payment Transactions (one-to-many per GRN) ───────────

  async listPaymentTransactions(inwardId) {
    return this.apiClient.get(`/api/v1/pharmacy/inward/${inwardId}/payment/transactions`);
  }

  async addPaymentTransaction(inwardId, data) {
    return this.apiClient.post(`/api/v1/pharmacy/inward/${inwardId}/payment/transactions`, data);
  }

  async updatePaymentTransaction(inwardId, txnId, data) {
    return this.apiClient.put(`/api/v1/pharmacy/inward/${inwardId}/payment/transactions/${txnId}`, data);
  }

  async deletePaymentTransaction(inwardId, txnId) {
    return this.apiClient.delete(`/api/v1/pharmacy/inward/${inwardId}/payment/transactions/${txnId}`);
  }

  async finalizeInward(inwardId) {
    return this.apiClient.put(`/api/v1/pharmacy/inward/${inwardId}/finalize`);
  }

  async listInwardReturns(id) {
    return this.apiClient.get(`/api/v1/pharmacy/inward/${id}/returns`);
  }

  async createInwardReturn(id, data) {
    return this.apiClient.post(`/api/v1/pharmacy/inward/${id}/returns`, data);
  }

  async settleInward(id, data = {}) {
    return this.apiClient.put(`/api/v1/pharmacy/inward/${id}/settle`, data);
  }

  async listInwardDocuments(id) {
    return this.apiClient.get(`/api/v1/pharmacy/inward/${id}/documents`);
  }

  async uploadInwardDocument(id, formData) {
    return this.apiClient.post(`/api/v1/pharmacy/inward/${id}/documents`, formData);
  }

  async deleteInwardDocument(id, docId) {
    return this.apiClient.delete(`/api/v1/pharmacy/inward/${id}/documents/${docId}`);
  }

  async getInwardTimeline(id) {
    return this.apiClient.get(`/api/v1/pharmacy/inward/${id}/timeline`);
  }

  // ── E-way Bill ────────────────────────────────────────────

  async upsertEwayBill(id, data) {
    return this.apiClient.post(`/api/v1/pharmacy/inward/${id}/eway`, data);
  }

  // ── Credit / Debit Notes ──────────────────────────────────

  async listCdn(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/inward/cdn/list${qs ? `?${qs}` : ''}`);
  }

  async createCdn(data) {
    return this.apiClient.post('/api/v1/pharmacy/inward/cdn/create', data);
  }

  async updateCdn(cdnId, data) {
    return this.apiClient.put(`/api/v1/pharmacy/inward/cdn/${cdnId}`, data);
  }

  // ── Inventory ─────────────────────────────────────────────

  async getStock(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/inventory/stock${qs ? `?${qs}` : ''}`);
  }

  async getLowStock() {
    return this.apiClient.get('/api/v1/pharmacy/inventory/stock/low');
  }

  async getOutOfStock() {
    return this.apiClient.get('/api/v1/pharmacy/inventory/stock/out');
  }

  async getExpiringStock(days = 30) {
    return this.apiClient.get(`/api/v1/pharmacy/inventory/stock/expiring?days=${days}`);
  }

  async getInventoryTransactions(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/inventory/transactions${qs ? `?${qs}` : ''}`);
  }

  async adjustStock(data) {
    return this.apiClient.post('/api/v1/pharmacy/inventory/adjustment', data);
  }

  // ── Billing / Outward ─────────────────────────────────────

  async listBills(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/billing/bills${qs ? `?${qs}` : ''}`);
  }

  async getBill(id) {
    return this.apiClient.get(`/api/v1/pharmacy/billing/bills/${id}`);
  }

  async createBill(data) {
    return this.apiClient.post('/api/v1/pharmacy/billing/bills', data);
  }

  async updateBill(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/billing/bills/${id}`, data);
  }

  async finalizeBill(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/billing/bills/${id}/finalize`, data);
  }

  async cancelBill(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/billing/bills/${id}/cancel`, data);
  }

  async returnBillItems(id, data) {
    return this.apiClient.post(`/api/v1/pharmacy/billing/bills/${id}/returns`, data);
  }

  async getBillReturn(returnId) {
    return this.apiClient.get(`/api/v1/pharmacy/billing/returns/${returnId}`);
  }

  async listCreditNotes(contactId) {
    return this.apiClient.get(`/api/v1/pharmacy/billing/credit-notes?contact_id=${contactId}`);
  }

  async listAllCreditNotes(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/billing/all-credit-notes${qs ? `?${qs}` : ''}`);
  }

  async getCreditNote(id) {
    return this.apiClient.get(`/api/v1/pharmacy/billing/credit-notes/${id}`);
  }

  async settleCreditNote(id, data) {
    return this.apiClient.put(`/api/v1/pharmacy/billing/credit-notes/${id}/settle`, data);
  }

  async listAllPayments(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/billing/all-payments${qs ? `?${qs}` : ''}`);
  }

  async listCashLedger(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/billing/cash-ledger${qs ? `?${qs}` : ''}`);
  }

  async listAllReturns(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/billing/all-returns${qs ? `?${qs}` : ''}`);
  }

  async listBillReturns(billId) {
    return this.apiClient.get(`/api/v1/pharmacy/billing/bills/${billId}/returns`);
  }

  async getBillingTimeline(id) {
    return this.apiClient.get(`/api/v1/pharmacy/billing/bills/${id}/timeline`);
  }

  async listBillCreditNotes(billId) {
    return this.apiClient.get(`/api/v1/pharmacy/billing/bills/${billId}/credit-notes`);
  }

  async listDoctors(search = '') {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.apiClient.get(`/api/v1/pharmacy/billing/doctors${qs}`);
  }

  async createDoctor(data) {
    return this.apiClient.post('/api/v1/pharmacy/billing/doctors', data);
  }

  async getBillingConfig(slug) {
    return this.apiClient.get(`/api/v1/pharmacy/billing/config/${slug}`);
  }

  async searchBillingItems(q, locationId) {
    return this.apiClient.get(`/api/v1/pharmacy/billing/items/search?q=${encodeURIComponent(q)}&location_id=${locationId}`);
  }

  async searchBillingPatients(q, limit = 10) {
    return this.apiClient.get(`/api/v1/pharmacy/billing/patients/search?q=${encodeURIComponent(q)}&limit=${limit}`);
  }

  async listGuestPatients(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/billing/guest-patients${qs ? `?${qs}` : ''}`);
  }

  // ── Debit Notes ───────────────────────────────────────────

  async listDebitNotes(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/billing/debit-notes${qs ? `?${qs}` : ''}`);
  }

  async getDebitNote(id) {
    return this.apiClient.get(`/api/v1/pharmacy/billing/debit-notes/${id}`);
  }

  async createDebitNote(data) {
    return this.apiClient.post('/api/v1/pharmacy/billing/debit-notes', data);
  }

  async applyDebitNote(id) {
    return this.apiClient.put(`/api/v1/pharmacy/billing/debit-notes/${id}/apply`, {});
  }

  async cancelDebitNote(id, data = {}) {
    return this.apiClient.put(`/api/v1/pharmacy/billing/debit-notes/${id}/cancel`, data);
  }

  // ── Accounting / Journal ──────────────────────────────────

  async listJournalEntries(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/accounting/journal${qs ? `?${qs}` : ''}`);
  }

  async getJournalEntry(id) {
    return this.apiClient.get(`/api/v1/pharmacy/accounting/journal/${id}`);
  }

  async listLedgerAccounts(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/accounting/ledger${qs ? `?${qs}` : ''}`);
  }

  async getLedgerAccountStatement(id, params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/accounting/ledger/${id}/statement${qs ? `?${qs}` : ''}`);
  }

  async listKitties() {
    return this.apiClient.get('/api/v1/pharmacy/accounting/kitties');
  }

  async getPatientLedger(params = {}) {
    const qs = this._qs(params);
    return this.apiClient.get(`/api/v1/pharmacy/accounting/patient-ledger${qs ? `?${qs}` : ''}`);
  }
}
